import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { radius } from '@karehero/llama/theme/radius';

import { Box, Stack } from '@mui/material';

import { CAM_NAV, type CamNavItem } from '@/cam-admin/CamShell';
import ComputerFrame from '@/designs-dev/device/ComputerFrame';
import ScaleToFit from '@/designs-dev/device/ScaleToFit';
import { PhoneScreenContext } from '@/designs-dev/device/PhoneScreenContext';
import {
  computerScreenHeight,
  computerScreenWidth,
  type Viewport,
} from '@/designs-dev/device/viewport';
import { careSearchScenes } from '@/cam-admin/care-search/CareSearch.scenes';
import { notificationScenes } from '@/cam-admin/notifications/Notifications.scenes';
import {
  camIntakeScenes,
  type CamIntakeInitial,
} from '@/karehero-app/tell-us-once/CamIntake.scenes';
import CareCircles from '@/cam-admin/care-circles/CareCircles';
import ControlPanel from '@/cam-admin/control-panel/ControlPanel';
import { type CareSearchInitial } from '@/cam-admin/care-search/CareSearch';
import {
  type NotificationsInitial,
  type TabId,
} from '@/cam-admin/control-panel/ControlPanel';
import {
  InspectProvider,
  useInspect,
} from '@/designs-dev/inspect/InspectContext';
import InspectOverlay from '@/designs-dev/inspect/InspectOverlay';
import InspectPanel from '@/designs-dev/inspect/InspectPanel';
import LayersPanel from '@/designs-dev/inspect/LayersPanel';
import DrillModeToggle from '@/designs-dev/inspect/DrillModeToggle';
import StageBar, { STAGE_BAR_ROOM } from '@/designs-dev/inspect/StageBar';
import StateSelector from '@/designs-dev/inspect/StateSelector';

// The CAM care search prototype on a centred stage inside the brand laptop
// frame, matching how the member app sits in PhoneFrame. The chrome floating
// at the foot of the stage is prototype-only: drill mode toggle, then state
// switcher, with the inspect panel docked beside the stage.
// An address that is never routed to: the stage intercepts it and switches the
// on-screen section instead, so a project's own CAM mount can reach the circles
// index without leaving the project's route (and without Loft's menu jumping to
// CAM Admin). Same trick as descopingNav's in-prototype paths.
const IN_PROJECT_CIRCLES = '#care-circles';

const Stage = ({
  initialView = 'panel',
  defaultTab,
  defaultIntake,
  sceneSet = 'care-search',
  inProjectCircles = false,
}: {
  initialView?: 'panel' | 'notifications';
  /** Where this mount opens when the URL does not say. See `?tab`. */
  defaultTab?: TabId;
  /** Whether this mount has an intake behind it. See `?intake`. */
  defaultIntake?: 'consented' | 'declined';
  /**
   * Which catalog the state select offers. A mount that opens on Overview wants
   * the intake states: the care-search scenes are states of a tab it is not on,
   * so choosing one changed nothing you could see.
   */
  sceneSet?: 'care-search' | 'intake';
  /**
   * Keep Care circles inside this mount.
   *
   * A project that borrows the CAM admin needs somewhere to go when the rail
   * says Care circles, and CAM Admin's own route is the wrong answer: it leaves
   * the project, moves the menu selection and loses the intake this mount was
   * opened with. On, the rail switches an in-page section and the URL does not
   * change.
   */
  inProjectCircles?: boolean;
}) => {
  const { active, current, setActive, setCurrent, setHovered } = useInspect();
  const navigate = useNavigate();

  // Which page the rail is showing, where the rail stays in the project.
  const [section, setSection] = useState<'panel' | 'circles'>('panel');

  // The rail, with Care circles repointed at the sentinel above.
  const projectNav: CamNavItem[] | undefined = inProjectCircles
    ? CAM_NAV.map((item) =>
        item.label === 'Care circles'
          ? { ...item, path: IN_PROJECT_CIRCLES }
          : item,
      )
    : undefined;

  // The sentinel switches sections; everything else falls through to the
  // router, so links out of the prototype still work.
  const handleNavigate = (path: string) => {
    if (inProjectCircles && path === IN_PROJECT_CIRCLES) {
      return setSection('circles');
    }
    navigate(path);
  };

  // The stage hosts two pages, switched by the menu via initialView: the
  // control-panel workspace and the global notifications feed. The state
  // switcher names the page you're on and offers that page's scenes.
  const isNotifications = initialView === 'notifications';
  const isIntake = !isNotifications && sceneSet === 'intake';
  const pageLabel = isNotifications ? 'Notifications' : 'Control panel';
  const scenes = isNotifications
    ? notificationScenes
    : isIntake
      ? camIntakeScenes
      : careSearchScenes;
  const defaultSceneId = isNotifications
    ? 'feed'
    : isIntake
      ? 'consented'
      : 'draft';

  const [searchParams, setSearchParams] = useSearchParams();
  // Which control-panel tab to open on. Opening a circle from the care circles
  // index lands on Overview (?tab=overview); the plain Control panel menu entry
  // has no tab and keeps the panel's own default.
  const initialTab =
    (searchParams.get('tab') as TabId | null) ?? defaultTab ?? undefined;
  // Which provider's full page to open on, by name. Read from the URL the same way
  // the tab is, so the shortlist drawer can link straight to a provider's record in
  // a new tab — the check a CAM makes before putting a home in front of a family.
  const initialProvider = searchParams.get('provider') ?? undefined;
  // Whether a Tell Us Once intake sits behind the circle. Opt-in from the URL,
  // the way the tab is: CAM Admin's own Control panel entry renders the overview
  // as it ships, and the Tell Us Once row asks for the intake because that is
  // the delta that project is about. Clamped, so an unknown value renders the
  // shipped state rather than a third thing.
  const intakeParam = searchParams.get('intake');
  const intake =
    intakeParam === 'consented' || intakeParam === 'declined'
      ? intakeParam
      : defaultIntake;
  const [sceneId, setSceneId] = useState(
    () => searchParams.get('scene') ?? defaultSceneId,
  );
  // Switching pages (same stage instance, new initialView) resets to the new
  // page's default scene — but not on first mount, so a deep-linked ?scene
  // survives.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    setSceneId(defaultSceneId);
  }, [defaultSceneId]);

  // Clamp to a scene that exists on the current page, so a stale or unknown
  // ?scene (e.g. a care-search id on the notifications page) never breaks.
  const currentScene = scenes.find((s) => s.id === sceneId) ?? scenes[0];

  // Reflect the active scene in the URL so the view is a shareable address.
  useEffect(() => {
    if (searchParams.get('scene') !== currentScene.id) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('scene', currentScene.id);
          return next;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScene.id]);

  // Laptop mockup, full-bleed fill, or pinned to a breakpoint width.
  // A web design opens at the laptop screen's width with no laptop drawn round
  // it: same screen as the frame, judged at the size it renders. The frame is
  // still a click away in the toggle for showing the thing off.
  const [viewport, setViewport] = useState<Viewport>(computerScreenWidth);
  const framed = viewport === 'frame';
  // Screen element for drill mode when unframed (ComputerFrame provides its
  // own when framed).
  const [fullEl, setFullEl] = useState<HTMLElement | null>(null);

  // Esc is staged: a first press clears a selected layer, a second (nothing
  // selected) leaves drill mode. Matches the phone stage.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (current) {
        setCurrent(null);
        setHovered(null);
      } else {
        setActive(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, current, setActive, setCurrent, setHovered]);

  // Prototype-only control: the state switcher, in the bar at the stage's foot.
  // Its options are the current page's scenes, from the shared catalogs.
  // Switching state clears any pinned component, so a ?select doesn't linger
  // onto the new (remounted) scene's nodes.
  const pickScene = (id: string) => {
    setCurrent(null);
    setHovered(null);
    setSceneId(id);
  };

  // key remounts the prototype in the chosen state. The selected scene drives
  // the care-search tab on the panel page, or the feed on the notifications page.
  const panel = (
    <ControlPanel
      key={currentScene.id}
      embedded
      initialTab={initialTab}
      initialView={initialView}
      intake={
        isIntake ? (currentScene.initial as CamIntakeInitial).intake : intake
      }
      nav={projectNav}
      onNavigate={handleNavigate}
      careSearch={
        isNotifications || isIntake
          ? undefined
          : // ?provider wins over the scene's own detailProvider: a CAM who
            // followed a link asked for that one provider, and the scene is only
            // saying where the page opens by default.
            {
              ...(currentScene.initial as CareSearchInitial),
              ...(initialProvider ? { detailProvider: initialProvider } : {}),
            }
      }
      notificationsInitial={
        isNotifications
          ? (currentScene.initial as NotificationsInitial)
          : undefined
      }
    />
  );

  // The circles index, shown with the same rail so the menu does not move and
  // opening a circle comes back to the panel rather than routing away.
  const circles = (
    <CareCircles
      nav={projectNav}
      onNavigate={handleNavigate}
      onOpenCircle={() => setSection('panel')}
    />
  );

  const prototype = section === 'circles' && inProjectCircles ? circles : panel;

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: active ? 'custom.charcoal' : 'custom.neutral.100',
        transition: 'background-color 400ms ease',
      }}
    >
      {/* The stage, with the controls floating over its foot. */}
      <Box sx={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex' }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            p: 3,
            // Room for the floating controls under the screen.
            pb: STAGE_BAR_ROOM,
            gap: 1.5,
            overflow: 'auto',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              alignItems: framed ? 'center' : 'stretch',
              justifyContent: 'center',
            }}
          >
            {/* The Stack must span the full stage width — the laptop measures
                its wrapper to size itself, and an auto-width parent would make
                it measure (and feed back on) its own width. */}
            <Stack
              sx={{ alignItems: 'center', width: '100%', minHeight: 0 }}
              spacing={2}
            >
              {framed ? (
                <ComputerFrame>
                  {prototype}
                  <InspectOverlay />
                </ComputerFrame>
              ) : (
                /* Out of the frame. 'fill' takes the stage's own width and
                 height; a fixed width lays out at that width and is scaled
                 down to whatever room there is, so a 1440 design is judged at
                 1440 without running off the side of the stage. */
                <ScaleToFit
                  width={viewport === 'fill' ? 'fill' : viewport}
                  height={
                    viewport === 'fill' ? undefined : computerScreenHeight
                  }
                >
                  <Box
                    sx={{
                      position: 'relative',
                      height: '100%',
                      width: '100%',
                      overflow: 'hidden',
                      borderRadius: `${radius.md}px`,
                      border: 1,
                      borderColor: 'custom.neutral.200',
                      bgcolor: 'background.default',
                    }}
                    ref={(el: HTMLElement | null) => setFullEl(el)}
                  >
                    <PhoneScreenContext.Provider value={fullEl}>
                      {prototype}
                      <InspectOverlay />
                    </PhoneScreenContext.Provider>
                  </Box>
                </ScaleToFit>
              )}
            </Stack>
          </Box>
        </Box>

        {/* Two pills, side by side, floating at the foot of the stage: how
            you are looking at the design, then what it is showing. */}
        <StageBar>
          <DrillModeToggle viewport={viewport} onViewportChange={setViewport} />
          <StateSelector
            scenes={scenes}
            sceneId={currentScene.id}
            onSceneChange={pickScene}
          />
        </StageBar>
      </Box>

      <LayersPanel />
      <InspectPanel />
    </Box>
  );
};

const CareSearchStage = ({
  initialView = 'panel',
  defaultTab,
  defaultIntake,
  sceneSet,
  inProjectCircles,
}: {
  initialView?: 'panel' | 'notifications';
  defaultTab?: TabId;
  defaultIntake?: 'consented' | 'declined';
  sceneSet?: 'care-search' | 'intake';
  /** See Stage. Keeps the rail's Care circles inside this mount. */
  inProjectCircles?: boolean;
}) => (
  <InspectProvider>
    <Stage
      initialView={initialView}
      defaultTab={defaultTab}
      defaultIntake={defaultIntake}
      sceneSet={sceneSet}
      inProjectCircles={inProjectCircles}
    />
  </InspectProvider>
);

export default CareSearchStage;
